import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
    Tooltip,
  MenuItem,
  Breadcrumbs,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Autocomplete,
  FormHelperText,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Games as AmenityIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BookOnline as BookOnlineIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Build as BuildIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { amenityAPI, amenityTypeAPI, membershipTierAPI, tenantSettingsAPI, equipmentAPI } from '../../services/api';
import BookingWindowConfiguration from '../common/BookingWindowConfiguration';
import BookAmenityDialog from './BookAmenityDialog';
import ReserveAmenityDialog from './ReserveAmenityDialog';
import { Amenity, AmenityType, PricingTier, TierPricing, AmenityAdditionalInfo, BookingWindowConfiguration as BookingWindowConfigurationType, MembershipTier, UserRole, TenantSettings, TierAccessConfiguration, TierAccessLevel, Equipment } from '../../types';
import AdminAccessControl from '../common/AdminAccessControl';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AmenityManagementPage: React.FC = () => {
  const { user } = useAuth();
  
  // Ref for dialog content scrolling
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  
  // State for amenities
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [amenityTypes, setAmenityTypes] = useState<AmenityType[]>([]);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBookDialog, setOpenBookDialog] = useState(false);
  const [openReserveDialog, setOpenReserveDialog] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [availableFilter, setAvailableFilter] = useState<string>(''); // '', 'true', 'false'

  // Maintenance dialog states
  const [openMaintenanceDialog, setOpenMaintenanceDialog] = useState(false);
  const [openMaintenanceConfirmDialog, setOpenMaintenanceConfirmDialog] = useState(false);
  const [maintenanceFormData, setMaintenanceFormData] = useState({
    date: '',
    startTime: '',
    endTime: '',
    reason: '',
    amenities: [] as string[]
  });
  const [schedulingMaintenance, setSchedulingMaintenance] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: '',
    newType: '',
    location: '',
    available: true,
    bookingFee: '',
    tierPricing: {
      [PricingTier.OFF_PEAK]: 0,
      [PricingTier.NORMAL]: 0,
      [PricingTier.PEAK]: 0
    } as TierPricing,
    maxBookingsPerSlot: 1,
    image: '',
    additionalInfo: {
      equipmentAvailable: false,
      equipmentList: [] as string[],
      rules: [] as string[],
      restrictions: [] as string[],
      amenities: [] as string[]
    } as AmenityAdditionalInfo,
    bookingWindows: {} as BookingWindowConfigurationType,
    tierAccess: {} as TierAccessConfiguration,
    equipmentIds: [] as string[]
  });

  // Form helper states
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment[]>([]);
  const [newRule, setNewRule] = useState('');
  const [newRestriction, setNewRestriction] = useState('');
  const [newAmenityFeature, setNewAmenityFeature] = useState('');

  // Collapsible section states
  const [bookingConfigExpanded, setBookingConfigExpanded] = useState(false);
  const [tierPricingExpanded, setTierPricingExpanded] = useState(false);

  // Booking window configuration state
  const [bookingWindowCards, setBookingWindowCards] = useState<Array<{
    id: string;
    membershipTier: string;
    unlimited: boolean;
    days: number;
  }>>([]);

  // Fetch data on component mount
  useEffect(() => {
    fetchAmenities();
    fetchAmenityTypes();
    fetchMembershipTiers();
    fetchTenantSettings();
    fetchEquipment();
  }, []);

  const fetchAmenities = async () => {
    try {
      setLoading(true);
      const response = await amenityAPI.getAmenities();
      setAmenities(response.data);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      setError('Failed to fetch amenities');
    } finally {
      setLoading(false);
    }
  };

  const fetchAmenityTypes = async () => {
    try {
      const response = await amenityTypeAPI.getAll();
      setAmenityTypes(response.data);
    } catch (error: any) {
      console.error('Error fetching amenity types:', error);
    }
  };

  const fetchMembershipTiers = async () => {
    try {
      const response = await membershipTierAPI.getMembershipTiers(true);
      setMembershipTiers(response.data.filter((tier: MembershipTier) => tier.active));
    } catch (error: any) {
      console.error('Error fetching membership tiers:', error);
    }
  };

  const fetchTenantSettings = async () => {
    try {
      const response = await tenantSettingsAPI.getAllSettings();
      setTenantSettings(response.data);
    } catch (error: any) {
      console.error('Error fetching tenant settings:', error);
      // Don't show error to user, just log it as this is not critical for amenity management
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await equipmentAPI.getAll(true); // Get only active equipment
      setEquipment(response.data);
    } catch (error: any) {
      console.error('Error fetching equipment:', error);
    }
  };

  const handleCreateAmenity = () => {
    setIsEditing(false);
    setError(null);
    
    // Use default booking windows from tenant settings
    const defaultBookingWindows = tenantSettings?.default_booking_windows || {};
    
    // Initialize tier access with default "Paid Access" for all membership tiers
    const defaultTierAccess: TierAccessConfiguration = {};
    membershipTiers.forEach(tier => {
      defaultTierAccess[tier.name] = TierAccessLevel.PAID;
    });
    
    setFormData({
      name: '',
      description: '',
      type: '',
      newType: '',
      location: '',
      available: true,
      bookingFee: '',
      tierPricing: {
        [PricingTier.OFF_PEAK]: 0,
        [PricingTier.NORMAL]: 0,
        [PricingTier.PEAK]: 0
      },
      maxBookingsPerSlot: 1,
      image: '',
      additionalInfo: {
        equipmentAvailable: false,
        equipmentList: [],
        rules: [],
        restrictions: [],
        amenities: []
      },
      bookingWindows: defaultBookingWindows,
      tierAccess: defaultTierAccess,
      equipmentIds: []
    });
    
    // Initialize booking window cards from default settings
    const windowCards = Object.entries(defaultBookingWindows).map(([tierName, window], index) => ({
      id: `card-${Date.now()}-${index}`,
      membershipTier: tierName,
      unlimited: window.unlimited || false,
      days: window.advanceBookingDays || 7
    }));
    setBookingWindowCards(windowCards);
    
    setSelectedEquipment([]);
    setNewRule('');
    setNewRestriction('');
    setNewAmenityFeature('');
    setBookingConfigExpanded(false);
    setTierPricingExpanded(false);
    setOpenCreateDialog(true);
  };

  const handleEditAmenity = (amenity: Amenity) => {
    setIsEditing(true);
    setSelectedAmenity(amenity);
    setError(null);
    
    setFormData({
      name: amenity.name,
      description: amenity.description || '',
      type: amenity.type,
      newType: '',
      location: amenity.location || '',
      available: amenity.available,
      bookingFee: amenity.bookingFee?.toString() || '',
      tierPricing: amenity.tierPricing || {
        [PricingTier.OFF_PEAK]: 0,
        [PricingTier.NORMAL]: 0,
        [PricingTier.PEAK]: 0
      },
      maxBookingsPerSlot: amenity.maxBookingsPerSlot,
      image: amenity.image || '',
      additionalInfo: amenity.additionalInfo || {
        equipmentAvailable: false,
        equipmentList: [],
        rules: [],
        restrictions: [],
        amenities: []
      },
      bookingWindows: amenity.bookingWindows || {},
      tierAccess: amenity.tierAccess || {},
      equipmentIds: amenity.equipment?.map(eq => eq.id) || []
    });
    
    // Initialize booking window cards from existing data
    const existingWindows = amenity.bookingWindows || {};
    const windowCards = Object.entries(existingWindows).map(([tierName, window], index) => ({
      id: `card-${Date.now()}-${index}`,
      membershipTier: tierName,
      unlimited: window.unlimited || false,
      days: window.advanceBookingDays || 7
    }));
    setBookingWindowCards(windowCards);
    
    // Set selected equipment for editing
    setSelectedEquipment(amenity.equipment || []);
    setNewRule('');
    setNewRestriction('');
    setNewAmenityFeature('');
    setBookingConfigExpanded(false);
    setTierPricingExpanded(false);
    setOpenCreateDialog(true);
  };

  const handleDeleteAmenity = (amenity: Amenity) => {
    setSelectedAmenity(amenity);
    setOpenDeleteDialog(true);
  };

  const handleBookAmenity = (amenity: Amenity) => {
    setSelectedAmenity(amenity);
    setOpenBookDialog(true);
  };

  const handleReserveAmenity = (amenity: Amenity) => {
    setSelectedAmenity(amenity);
    setOpenReserveDialog(true);
  };

  const confirmDeleteAmenity = async () => {
    if (!selectedAmenity) return;

    try {
      await amenityAPI.deleteAmenity(selectedAmenity.id);
      await fetchAmenities();
      setOpenDeleteDialog(false);
      setSelectedAmenity(null);
      setSuccessMessage('Amenity deleted successfully');
    } catch (error: any) {
      console.error('Error deleting amenity:', error);
      setError('Failed to delete amenity');
    }
  };

  const handleBookingSuccess = (message?: string) => {
    setOpenBookDialog(false);
    setSelectedAmenity(null);
    setSuccessMessage(message || 'Amenity booked successfully');
  };

  const handleReservationSuccess = (message?: string) => {
    setOpenReserveDialog(false);
    setSelectedAmenity(null);
    setSuccessMessage(message || 'Amenity reserved successfully');
  };

  // Check if user can reserve amenities (super admin, admin, employee, coach)
  const canReserveAmenities = () => {
    return user && [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.EMPLOYEE,
      UserRole.COACH
    ].includes(user.role);
  };

  const handleOpenMaintenanceDialog = () => {
    setMaintenanceFormData({
      date: '',
      startTime: '',
      endTime: '',
      reason: '',
      amenities: []
    });
    setError(null);
    setOpenMaintenanceDialog(true);
  };

  const handleMaintenanceFormChange = (field: string, value: any) => {
    setMaintenanceFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleScheduleMaintenance = () => {
    // Validate all fields
    if (!maintenanceFormData.date) {
      setError('Date is required');
      return;
    }
    if (!maintenanceFormData.startTime) {
      setError('Start time is required');
      return;
    }
    if (!maintenanceFormData.endTime) {
      setError('End time is required');
      return;
    }
    if (!maintenanceFormData.reason.trim()) {
      setError('Reason for cancellation is required');
      return;
    }
    if (maintenanceFormData.amenities.length === 0) {
      setError('At least one amenity must be selected');
      return;
    }

    // Validate time range
    if (maintenanceFormData.startTime >= maintenanceFormData.endTime) {
      setError('End time must be after start time');
      return;
    }

    setError(null);
    setOpenMaintenanceDialog(false);
    setOpenMaintenanceConfirmDialog(true);
  };

  const confirmScheduleMaintenance = async () => {
    try {
      setSchedulingMaintenance(true);
      setError(null);

      const response = await amenityAPI.scheduleMaintenance(maintenanceFormData);

      const { cancelledBookingsCount } = response.data;

      setOpenMaintenanceConfirmDialog(false);
      setMaintenanceFormData({
        date: '',
        startTime: '',
        endTime: '',
        reason: '',
        amenities: []
      });

      setSuccessMessage(
        `Maintenance scheduled successfully. ${cancelledBookingsCount} booking(s) cancelled and users notified.`
      );

      // Refresh amenities list
      await fetchAmenities();
    } catch (error: any) {
      console.error('Error scheduling maintenance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to schedule maintenance';
      setError(errorMessage);
      setOpenMaintenanceConfirmDialog(false);
    } finally {
      setSchedulingMaintenance(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    
    // Validation: ensure name is provided
    if (!formData.name.trim()) {
      setError('Amenity name is required');
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Validation: ensure type is provided
    if (!formData.type.trim()) {
      setError('Amenity type is required');
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    try {
      // Create new amenity type if needed
      let finalType = formData.type;
      if (formData.newType.trim()) {
        await amenityTypeAPI.create({
          name: formData.newType.trim(),
          description: `Auto-created type for ${formData.newType.trim()}`,
          active: true
        });
        finalType = formData.newType.trim();
        await fetchAmenityTypes();
      }

      const amenityData = {
        name: formData.name,
        description: formData.description,
        type: finalType,
        location: formData.location,
        available: formData.available,
        bookingFee: formData.bookingFee ? parseFloat(formData.bookingFee) : null,
        tierPricing: formData.tierPricing,
        maxBookingsPerSlot: formData.maxBookingsPerSlot,
        image: formData.image,
        additionalInfo: formData.additionalInfo,
        bookingWindows: formData.bookingWindows,
        tierAccess: formData.tierAccess,
        equipmentIds: selectedEquipment.map(eq => eq.id)
      };

      console.log('Submitting amenity data:', amenityData);

      if (isEditing && selectedAmenity) {
        await amenityAPI.updateAmenity(selectedAmenity.id, amenityData);
        setSuccessMessage('Amenity updated successfully');
      } else {
        await amenityAPI.createAmenity(amenityData);
        setSuccessMessage('Amenity created successfully');
      }

      await fetchAmenities();
      setOpenCreateDialog(false);
    } catch (error: any) {
      console.error('Error saving amenity:', error);
      setError(isEditing ? 'Failed to update amenity' : 'Failed to create amenity');
    }
  };

  const addRule = () => {
    if (newRule.trim()) {
      setFormData({
        ...formData,
        additionalInfo: {
          ...formData.additionalInfo,
          rules: [...(formData.additionalInfo.rules || []), newRule.trim()]
        }
      });
      setNewRule('');
    }
  };

  const removeRule = (index: number) => {
    const newList = [...(formData.additionalInfo.rules || [])];
    newList.splice(index, 1);
    setFormData({
      ...formData,
      additionalInfo: {
        ...formData.additionalInfo,
        rules: newList
      }
    });
  };

  const addRestriction = () => {
    if (newRestriction.trim()) {
      setFormData({
        ...formData,
        additionalInfo: {
          ...formData.additionalInfo,
          restrictions: [...(formData.additionalInfo.restrictions || []), newRestriction.trim()]
        }
      });
      setNewRestriction('');
    }
  };

  const removeRestriction = (index: number) => {
    const newList = [...(formData.additionalInfo.restrictions || [])];
    newList.splice(index, 1);
    setFormData({
      ...formData,
      additionalInfo: {
        ...formData.additionalInfo,
        restrictions: newList
      }
    });
  };

  const addAmenityFeature = () => {
    if (newAmenityFeature.trim()) {
      setFormData({
        ...formData,
        additionalInfo: {
          ...formData.additionalInfo,
          amenities: [...(formData.additionalInfo.amenities || []), newAmenityFeature.trim()]
        }
      });
      setNewAmenityFeature('');
    }
  };

  const removeAmenityFeature = (index: number) => {
    const newList = [...(formData.additionalInfo.amenities || [])];
    newList.splice(index, 1);
    setFormData({
      ...formData,
      additionalInfo: {
        ...formData.additionalInfo,
        amenities: newList
      }
    });
  };

  // Booking window card management functions
  const addBookingWindowCard = () => {
    const newCard = {
      id: `card-${Date.now()}`,
      membershipTier: '',
      unlimited: false,
      days: 7
    };
    setBookingWindowCards([...bookingWindowCards, newCard]);
  };

  const removeBookingWindowCard = (cardId: string) => {
    const updatedCards = bookingWindowCards.filter(card => card.id !== cardId);
    setBookingWindowCards(updatedCards);
    
    // Update form data - remove the booking window for this card's membership tier
    const cardToRemove = bookingWindowCards.find(card => card.id === cardId);
    if (cardToRemove && cardToRemove.membershipTier) {
      const { [cardToRemove.membershipTier]: removed, ...remainingWindows } = formData.bookingWindows;
      setFormData({
        ...formData,
        bookingWindows: remainingWindows
      });
    }
  };

  const updateBookingWindowCard = (cardId: string, field: string, value: any) => {
    const updatedCards = bookingWindowCards.map(card => {
      if (card.id === cardId) {
        const updatedCard = { ...card, [field]: value };
        
        // If membership tier changed, remove the old booking window from formData
        if (field === 'membershipTier' && card.membershipTier && card.membershipTier !== value) {
          const { [card.membershipTier]: removed, ...remainingWindows } = formData.bookingWindows;
          setFormData({
            ...formData,
            bookingWindows: remainingWindows
          });
        }
        
        // Update formData with the new booking window configuration
        if (updatedCard.membershipTier) {
          setFormData({
            ...formData,
            bookingWindows: {
              ...formData.bookingWindows,
              [updatedCard.membershipTier]: {
                advanceBookingDays: updatedCard.unlimited ? 0 : updatedCard.days,
                unlimited: updatedCard.unlimited
              }
            }
          });
        }
        
        return updatedCard;
      }
      return card;
    });
    setBookingWindowCards(updatedCards);
  };

  // Filter amenities based on search and filter criteria
  const filteredAmenities = useMemo(() => {
    let filtered = [...amenities];

    // Text search (name or description)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(amenity =>
        amenity.name.toLowerCase().includes(searchLower) ||
        (amenity.description && amenity.description.toLowerCase().includes(searchLower))
      );
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter(amenity => amenity.type === typeFilter);
    }

    // Available filter
    if (availableFilter === 'true') {
      filtered = filtered.filter(amenity => amenity.available === true);
    } else if (availableFilter === 'false') {
      filtered = filtered.filter(amenity => amenity.available === false);
    }

    return filtered;
  }, [amenities, searchText, typeFilter, availableFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setTypeFilter('');
    setAvailableFilter('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <AdminAccessControl allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.COACH]}>
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Typography color="text.primary">Amenity Management</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            <AmenityIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Amenity Management
          </Typography>
          <Stack direction="row" spacing={2}>
            <Tooltip title="View Courts Calendar">
              <Button
                component={RouterLink}
                to="/courts-calendar"
                variant="outlined"
                startIcon={<ScheduleIcon />}
              >
                Courts Calendar
              </Button>
            </Tooltip>
            <Tooltip title="Refresh Amenity List">
            <Button
              variant="outlined"
              onClick={fetchAmenities}
              disabled={loading}
            >
              <RefreshIcon />
            </Button>
            </Tooltip>
            <Tooltip title="Schedule maintenance to cancel bookings">
              <Button
                variant="outlined"
                color="warning"
                startIcon={<BuildIcon />}
                onClick={handleOpenMaintenanceDialog}
              >
                Maintenance
              </Button>
            </Tooltip>
            <Tooltip title="Create new amenity">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateAmenity}
            >
              Amenity
            </Button>
            </Tooltip>
          </Stack>
        </Box>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon />
              <Typography variant="h6">Filters</Typography>
              {(searchText || typeFilter || availableFilter) && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{ ml: 'auto' }}
                >
                  Clear All
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              {/* Text Search */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Search by name or description..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Type Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Amenity Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Amenity Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {amenityTypes.map((type) => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Available Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Availability</InputLabel>
                  <Select
                    value={availableFilter}
                    onChange={(e) => setAvailableFilter(e.target.value)}
                    label="Availability"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Available</MenuItem>
                    <MenuItem value="false">Unavailable</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Results count */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredAmenities.length} of {amenities.length} amenities
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Amenities Grid */}
        <Grid container spacing={3}>
          {filteredAmenities.map((amenity) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={amenity.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      noWrap
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                      }}
                      onClick={() => handleEditAmenity(amenity)}
                      title="Click to edit amenity"
                    >
                      {amenity.name}
                    </Typography>
                    <Chip
                      label={amenity.available ? 'Available' : 'Unavailable'}
                      color={amenity.available ? 'success' : 'default'}
                      size="small"
                      icon={amenity.available ? <CheckCircleIcon /> : <CancelIcon />}
                    />
                  </Box>
                  
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AmenityIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Type: {amenity.type}
                      </Typography>
                    </Box>
                    
                    {amenity.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {amenity.location}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InfoIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Max bookings per slot: {amenity.maxBookingsPerSlot}
                      </Typography>
                    </Box>
                    
                    {amenity.bookingFee && amenity.bookingFee > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Base fee: ${amenity.bookingFee}
                        </Typography>
                      </Box>
                    )}
                    
                    {amenity.tierPricing && Object.keys(amenity.tierPricing).length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Tier pricing available
                        </Typography>
                      </Box>
                    )}
                    
                    {amenity.image && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ImageIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Has image
                        </Typography>
                      </Box>
                    )}
                    
                    {amenity.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {amenity.description}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'center', px: 2, pb: 2 }}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/amenities/${amenity.id}/schedule`}
                      startIcon={<ScheduleIcon />}
                    >
                      Schedule
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<BookOnlineIcon />}
                      onClick={() => handleBookAmenity(amenity)}
                    >
                      Book
                    </Button>
                    {canReserveAmenities() && (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={<BookOnlineIcon />}
                        onClick={() => handleReserveAmenity(amenity)}
                      >
                        Reserve
                      </Button>
                    )}
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {amenities.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <AmenityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No amenities found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first amenity to get started
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateAmenity}>
              Create Amenity
            </Button>
          </Paper>
        )}

        {amenities.length > 0 && filteredAmenities.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <FilterListIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No amenities match your filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try adjusting your search criteria
            </Typography>
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters}>
              Clear Filters
            </Button>
          </Paper>
        )}

        {/* Create/Edit Dialog */}
        <Dialog 
          open={openCreateDialog} 
          onClose={(event, reason) => {
            if (reason === 'backdropClick') {
              return;
            }
            setOpenCreateDialog(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isEditing ? 'Edit Amenity' : 'Create New Amenity'}
          </DialogTitle>
          <DialogContent ref={dialogContentRef}>
            {/* Error Alert inside Dialog */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Basic Information */}
              <Typography variant="h6">Basic Information</Typography>
              
              <TextField
                label="Amenity Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />

              {/* Amenity Type and Location on same line (50% each) */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={amenityTypes.filter(type => type.active).map((type) => type.name)}
                    value={formData.type}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, type: newValue || '', newType: '' });
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFormData({ ...formData, type: newInputValue, newType: newInputValue });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Amenity Type"
                        helperText="Select existing or type new"
                        required
                      />
                    )}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    fullWidth
                  />
                </Box>
              </Box>

              {/* Availability Toggle */}
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.available}
                      onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {formData.available ? 'Enabled' : 'Disabled'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.available 
                          ? 'Amenity is available for booking and reservations' 
                          : 'Amenity cannot be booked or reserved'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Collapsible Booking Configuration */}
              <Accordion expanded={bookingConfigExpanded} onChange={() => setBookingConfigExpanded(!bookingConfigExpanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Booking Configuration</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={3}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="Max Bookings Per Slot"
                        type="number"
                        value={formData.maxBookingsPerSlot}
                        onChange={(e) => setFormData({ ...formData, maxBookingsPerSlot: parseInt(e.target.value) || 1 })}
                        InputProps={{ inputProps: { min: 1 } }}
                        fullWidth
                      />

                      <TextField
                        label="Base Booking Fee"
                        type="number"
                        value={formData.bookingFee}
                        onChange={(e) => setFormData({ ...formData, bookingFee: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          inputProps: { min: 0, step: 0.01 }
                        }}
                        fullWidth
                      />
                    </Box>

                    {/* Booking Window Section */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Booking Window
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={addBookingWindowCard}
                        >
                          Add Window
                        </Button>
                      </Box>
                      
                      {bookingWindowCards.length === 0 ? (
                        <Alert severity="info">
                          No booking windows configured. Add booking windows to control how far in advance different membership tiers can book this amenity.
                        </Alert>
                      ) : (
                        <Stack spacing={2}>
                          {bookingWindowCards.map((card) => (
                            <Card key={card.id} variant="outlined">
                              <CardContent sx={{ pb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="subtitle2">Booking Window</Typography>
                                  <IconButton 
                                    color="error" 
                                    size="small" 
                                    onClick={() => removeBookingWindowCard(card.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                
                                <Stack spacing={2}>
                                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <FormControl sx={{ flex: 1 }} size="small">
                                      <InputLabel>Membership Tier</InputLabel>
                                      <Select
                                        value={card.membershipTier}
                                        label="Membership Tier"
                                        onChange={(e) => updateBookingWindowCard(card.id, 'membershipTier', e.target.value)}
                                      >
                                        {membershipTiers
                                          .filter(tier => !bookingWindowCards.some(otherCard => 
                                            otherCard.id !== card.id && otherCard.membershipTier === tier.name
                                          ))
                                          .map((tier) => (
                                            <MenuItem key={tier.id} value={tier.name}>
                                              {tier.name}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                    </FormControl>
                                    
                                    {!card.unlimited && (
                                      <TextField
                                        label="Advance Booking Days"
                                        type="number"
                                        value={card.days}
                                        onChange={(e) => {
                                          const value = parseInt(e.target.value, 10);
                                          if (!isNaN(value) && value > 0) {
                                            updateBookingWindowCard(card.id, 'days', value);
                                          }
                                        }}
                                        size="small"
                                        sx={{ width: 180 }}
                                        InputProps={{
                                          inputProps: { min: 1, max: 365 }
                                        }}
                                      />
                                    )}
                                  </Box>
                                  
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={card.unlimited}
                                        onChange={(e) => updateBookingWindowCard(card.id, 'unlimited', e.target.checked)}
                                      />
                                    }
                                    label="Unlimited advance booking"
                                  />
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Collapsible Tier Pricing */}
              <Accordion expanded={tierPricingExpanded} onChange={() => setTierPricingExpanded(!tierPricingExpanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Tier Pricing</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Off-Peak Price"
                      type="number"
                      value={formData.tierPricing[PricingTier.OFF_PEAK]}
                      onChange={(e) => setFormData({
                        ...formData,
                        tierPricing: {
                          ...formData.tierPricing,
                          [PricingTier.OFF_PEAK]: parseFloat(e.target.value) || 0
                        }
                      })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: 0.01 }
                      }}
                      fullWidth
                    />

                    <TextField
                      label="Normal Price"
                      type="number"
                      value={formData.tierPricing[PricingTier.NORMAL]}
                      onChange={(e) => setFormData({
                        ...formData,
                        tierPricing: {
                          ...formData.tierPricing,
                          [PricingTier.NORMAL]: parseFloat(e.target.value) || 0
                        }
                      })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: 0.01 }
                      }}
                      fullWidth
                    />

                    <TextField
                      label="Peak Price"
                      type="number"
                      value={formData.tierPricing[PricingTier.PEAK]}
                      onChange={(e) => setFormData({
                        ...formData,
                        tierPricing: {
                          ...formData.tierPricing,
                          [PricingTier.PEAK]: parseFloat(e.target.value) || 0
                        }
                      })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: 0.01 }
                      }}
                      fullWidth
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Tier Access Control */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Tier Access Control</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Configure access levels for each membership tier. Members with "No Access" cannot book this amenity.
                  </Typography>
                  <Grid container spacing={2}>
                    {membershipTiers.map((tier) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tier.id}>
                        <FormControl fullWidth>
                          <InputLabel>{tier.name}</InputLabel>
                          <Select
                            value={formData.tierAccess[tier.name] || TierAccessLevel.PAID}
                            onChange={(e) => setFormData({
                              ...formData,
                              tierAccess: {
                                ...formData.tierAccess,
                                [tier.name]: e.target.value as TierAccessLevel
                              }
                            })}
                            label={tier.name}
                          >
                            <MenuItem value={TierAccessLevel.FREE}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                                Free Access
                              </Box>
                            </MenuItem>
                            <MenuItem value={TierAccessLevel.PAID}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AttachMoneyIcon color="info" fontSize="small" />
                                Paid Access
                              </Box>
                            </MenuItem>
                            <MenuItem value={TierAccessLevel.NO_ACCESS}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CancelIcon color="error" fontSize="small" />
                                No Access
                              </Box>
                            </MenuItem>
                          </Select>
                          <FormHelperText>
                            {formData.tierAccess[tier.name] === TierAccessLevel.FREE && 'Members can book without charge'}
                            {formData.tierAccess[tier.name] === TierAccessLevel.PAID && 'Members pay the configured booking fee'}
                            {formData.tierAccess[tier.name] === TierAccessLevel.NO_ACCESS && 'Members cannot book this amenity'}
                            {!formData.tierAccess[tier.name] && 'Default: Paid Access'}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Equipment & Features */}
              <Typography variant="h6">Equipment & Features</Typography>
              
              <Autocomplete
                multiple
                options={equipment}
                getOptionLabel={(option) => `${option.name} - $${option.costAmount}/${option.costUnit}min`}
                value={selectedEquipment}
                onChange={(event, newValue) => {
                  setSelectedEquipment(newValue);
                  setFormData({
                    ...formData,
                    equipmentIds: newValue.map(eq => eq.id)
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Equipment"
                    placeholder="Choose equipment available for this amenity"
                    helperText="Select equipment that can be booked with this amenity"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={`${option.name} ($${option.costAmount})`}
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />

              {/* Rules */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Add Rule"
                  value={newRule}
                  onChange={(e) => setNewRule(e.target.value)}
                  fullWidth
                />
                <Button onClick={addRule} variant="outlined">Add</Button>
              </Box>
              
              {formData.additionalInfo.rules && formData.additionalInfo.rules.length > 0 && (
                <Box>
                  {formData.additionalInfo.rules.map((rule, index) => (
                    <Chip
                      key={index}
                      label={rule}
                      onDelete={() => removeRule(index)}
                      sx={{ mr: 1, mb: 1 }}
                      color="info"
                    />
                  ))}
                </Box>
              )}

              {/* Restrictions */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Add Restriction"
                  value={newRestriction}
                  onChange={(e) => setNewRestriction(e.target.value)}
                  fullWidth
                />
                <Button onClick={addRestriction} variant="outlined">Add</Button>
              </Box>
              
              {formData.additionalInfo.restrictions && formData.additionalInfo.restrictions.length > 0 && (
                <Box>
                  {formData.additionalInfo.restrictions.map((restriction, index) => (
                    <Chip
                      key={index}
                      label={restriction}
                      onDelete={() => removeRestriction(index)}
                      sx={{ mr: 1, mb: 1 }}
                      color="warning"
                    />
                  ))}
                </Box>
              )}

              {/* Additional Amenity Features */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Add Amenity Feature"
                  value={newAmenityFeature}
                  onChange={(e) => setNewAmenityFeature(e.target.value)}
                  fullWidth
                />
                <Button onClick={addAmenityFeature} variant="outlined">Add</Button>
              </Box>
              
              {formData.additionalInfo.amenities && formData.additionalInfo.amenities.length > 0 && (
                <Box>
                  {formData.additionalInfo.amenities.map((feature, index) => (
                    <Chip
                      key={index}
                      label={feature}
                      onDelete={() => removeAmenityFeature(index)}
                      sx={{ mr: 1, mb: 1 }}
                      color="success"
                    />
                  ))}
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Box>
              {isEditing && (
                <Button 
                  onClick={() => {
                    setOpenCreateDialog(false);
                    setOpenDeleteDialog(true);
                  }}
                  color="error" 
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} variant="contained">
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Delete Amenity</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedAmenity?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={confirmDeleteAmenity} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Book Amenity Dialog */}
        <BookAmenityDialog
          open={openBookDialog}
          onClose={() => setOpenBookDialog(false)}
          amenity={selectedAmenity}
          onSuccess={handleBookingSuccess}
        />

        {/* Reserve Amenity Dialog */}
        <ReserveAmenityDialog
          open={openReserveDialog}
          onClose={() => setOpenReserveDialog(false)}
          amenity={selectedAmenity}
          onSuccess={handleReservationSuccess}
        />

        {/* Maintenance Scheduling Dialog */}
        <Dialog
          open={openMaintenanceDialog}
          onClose={() => {
            setOpenMaintenanceDialog(false);
            setError(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BuildIcon color="warning" />
              Schedule Maintenance
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Alert severity="info">
                Schedule maintenance to cancel existing bookings across multiple amenities.
              </Alert>

              <TextField
                label="Date"
                type="date"
                value={maintenanceFormData.date}
                onChange={(e) => handleMaintenanceFormChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />

              <TextField
                label="Start Time"
                type="time"
                value={maintenanceFormData.startTime}
                onChange={(e) => handleMaintenanceFormChange('startTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />

              <TextField
                label="End Time"
                type="time"
                value={maintenanceFormData.endTime}
                onChange={(e) => handleMaintenanceFormChange('endTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />

              <TextField
                label="Reason for Cancellation (provided to users)"
                value={maintenanceFormData.reason}
                onChange={(e) => handleMaintenanceFormChange('reason', e.target.value)}
                multiline
                rows={3}
                required
                fullWidth
                helperText="This message will be sent to users whose bookings are cancelled"
              />

              <Autocomplete
                multiple
                id="maintenance-amenities-select"
                options={amenities}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                value={amenities.filter(a => maintenanceFormData.amenities.includes(a.id))}
                onChange={(event, newValue) => {
                  handleMaintenanceFormChange('amenities', newValue.map(a => a.id));
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      <AmenityIcon fontSize="small" sx={{ mr: 1 }} />
                      {option.name}
                      {option.type && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({option.type})
                        </Typography>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Amenities"
                    placeholder="Choose amenities for maintenance"
                    required
                    error={maintenanceFormData.amenities.length === 0}
                    helperText="Select which amenities will be unavailable during maintenance"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.name}
                        {...chipProps}
                        size="small"
                      />
                    );
                  })
                }
              />

              {error && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenMaintenanceDialog(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleMaintenance}
              variant="contained"
              color="warning"
              startIcon={<BuildIcon />}
            >
              Schedule Maintenance
            </Button>
          </DialogActions>
        </Dialog>

        {/* Maintenance Confirmation Dialog */}
        <Dialog
          open={openMaintenanceConfirmDialog}
          onClose={() => !schedulingMaintenance && setOpenMaintenanceConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BuildIcon color="warning" />
              Confirm Maintenance Scheduling
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Alert severity="warning">
                This will cancel all existing bookings for the selected amenities during the specified time period.
              </Alert>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Date: {maintenanceFormData.date}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Time: {maintenanceFormData.startTime} - {maintenanceFormData.endTime}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Amenities:
                </Typography>
                <Box sx={{ ml: 2 }}>
                  {maintenanceFormData.amenities.map((amenityId) => {
                    const amenity = amenities.find(a => a.id === amenityId);
                    return (
                      <Typography key={amenityId} variant="body2">
                        • {amenity?.name || amenityId}
                      </Typography>
                    );
                  })}
                </Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Reason: {maintenanceFormData.reason}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Affected users will be notified via email about the cancellation.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenMaintenanceConfirmDialog(false)}
              disabled={schedulingMaintenance}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmScheduleMaintenance}
              variant="contained"
              color="warning"
              disabled={schedulingMaintenance}
              startIcon={schedulingMaintenance ? <CircularProgress size={20} /> : <BuildIcon />}
            >
              {schedulingMaintenance ? 'Scheduling...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      </AdminAccessControl>
    </Container>
  );
};

export default AmenityManagementPage;